/**
 * ft-app-footer
 * Split panel footer with CTA banner for registration.
 *
 * Attributes:
 * - cta-heading: CTA banner headline (default: "Start tracking institutional portfolios today")
 * - cta-subtext: CTA banner subtext (default: "Free access to 5,200+ fund holdings. No credit card required.")
 * - cta-button-text: CTA button label (default: "Create Free Account")
 * - cta-href: CTA button link (default: "https://auth.formthirteen.com/register")
 * - hide-cta: Boolean attribute to hide the CTA banner
 * - last-policy-update: Date when policies were last updated (YYYY-MM-DD format)
 *   If this date is after the user's acknowledgment date, re-acknowledgment is required.
 *
 * Slots:
 * - None (self-contained component)
 *
 * Events:
 * - ft-footer-cta-click: Fired when CTA button is clicked
 * - ft-consent-accept: Fired when user acknowledges terms. Detail: { timestamp, acceptedDate }
 */

import { icon } from "../../../shared/icons.js";

// Precondition: None
// Postcondition: Custom element class for app footer with CTA banner
class AppFooter extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" });
  }

  private static CONSENT_KEY = "ft-consent";

  // Precondition: Element is connected to DOM
  // Postcondition: Component is rendered with event listeners attached
  connectedCallback(): void {
    this.render();
    this.setupEventListeners();
    this.updateConsentDisplay();

    // Listen for consent updates from the banner
    window.addEventListener("ft-consent-updated", () =>
      this.updateConsentDisplay(),
    );
  }

  // Precondition: None
  // Postcondition: Event listeners are removed
  disconnectedCallback(): void {
    // Cleanup handled by shadow DOM removal
  }

  // Attribute getters with defaults
  get ctaHeading(): string {
    return (
      this.getAttribute("cta-heading") ||
      "Start tracking institutional portfolios today"
    );
  }

  get ctaSubtext(): string {
    return (
      this.getAttribute("cta-subtext") ||
      "Free access to 5,200+ fund holdings. No credit card required."
    );
  }

  get ctaButtonText(): string {
    return this.getAttribute("cta-button-text") || "Create Free Account";
  }

  get ctaHref(): string {
    return (
      this.getAttribute("cta-href") || "https://auth.formthirteen.com/register"
    );
  }

  get hideCta(): boolean {
    return this.hasAttribute("hide-cta");
  }

  // Returns the last policy update date as a Date object, or null if not set
  get lastPolicyUpdate(): Date | null {
    const attr = this.getAttribute("last-policy-update");
    if (!attr) return null;
    const date = new Date(attr + "T00:00:00");
    return isNaN(date.getTime()) ? null : date;
  }

  // Precondition: shadowRoot exists
  // Postcondition: Component HTML and styles are rendered
  private render(): void {
    const style = document.createElement("style");
    style.textContent = this.getStyles();

    const template = document.createElement("template");
    template.innerHTML = this.getTemplate();

    if (this.shadowRoot) {
      this.shadowRoot.innerHTML = "";
      this.shadowRoot.appendChild(style);
      this.shadowRoot.appendChild(template.content.cloneNode(true));
    }
  }

  private getStyles(): string {
    return `
      :host {
        display: block;
        --footer-bg: var(--surface, #f0f0f0);
        --footer-bg2: var(--bg2, #f4f4f5);
        --footer-text: var(--text, #1c1c1e);
        --footer-muted: var(--muted, #555555);
        --footer-color: var(--footer-color, #999999);
        --footer-primary: var(--primary, #e0524d);
        --footer-primary-pressed: var(--primary-pressed, #d53a2a);
        --footer-divider: var(--divider-color, #e2e4e8);
        --footer-success: #10b981;
      }

      @media (prefers-color-scheme: dark) {
        :host {
          --footer-bg: var(--surface, #14161a);
          --footer-bg2: var(--bg2, #111216);
          --footer-text: var(--text, #ffffff);
          --footer-muted: var(--muted, #a3a3a3);
          --footer-color: var(--footer-color, #8a8a8a);
          --footer-divider: var(--divider-color, #2a2d33);
        }
      }

      * {
        margin: 0;
        padding: 0;
        box-sizing: border-box;
      }

      .footer {
        background: var(--footer-bg);
        border-top: 1px solid var(--footer-divider);
      }

      /* CTA Banner */
      .cta-banner {
        padding: 32px;
        background: linear-gradient(135deg, var(--footer-primary) 0%, var(--footer-primary-pressed) 100%);
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 24px;
        flex-wrap: wrap;
      }

      .cta-content {
        display: flex;
        flex-direction: column;
        gap: 4px;
      }

      .cta-heading {
        font: 700 18px/1.2 "Space Grotesk", system-ui, sans-serif;
        color: white;
        letter-spacing: -0.01em;
      }

      .cta-subtext {
        font: 400 14px/1.4 Inter, system-ui, sans-serif;
        color: rgba(255, 255, 255, 0.85);
      }

      .cta-banner ft-button {
        --primary: white;
        --primary-text: var(--footer-primary);
      }

      /* Main footer content */
      .footer-main {
        padding: 48px 32px;
        display: grid;
        grid-template-columns: 1fr 2fr;
        gap: 64px;
      }

      .brand-section {
        display: flex;
        flex-direction: column;
        gap: 20px;
      }

      .brand {
        display: flex;
        align-items: center;
        gap: 12px;
      }

      .logo {
        width: 40px;
        height: 40px;
        background: var(--footer-primary);
        border-radius: var(--radius, 8px);
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 4px 12px rgba(224, 82, 77, 0.25);
      }

      .logo svg {
        width: 22px;
        height: 22px;
        fill: white;
      }

      .brand-info {
        display: flex;
        flex-direction: column;
        gap: 2px;
      }

      .brand-name {
        font: 700 20px/1 "Space Grotesk", system-ui, sans-serif;
        color: var(--footer-text);
        letter-spacing: -0.02em;
      }

      .brand-sub {
        font: 400 12px/1 Inter, system-ui, sans-serif;
        color: var(--footer-muted);
      }

      .tagline {
        font: 400 14px/1.6 Inter, system-ui, sans-serif;
        color: var(--footer-muted);
      }

      .trust-badges {
        display: flex;
        align-items: center;
        gap: 16px;
        flex-wrap: wrap;
      }

      .trust-badge {
        display: flex;
        align-items: center;
        gap: 6px;
        font: 500 11px/1 Inter, system-ui, sans-serif;
        color: var(--footer-muted);
      }

      .trust-badge svg {
        width: 14px;
        height: 14px;
        fill: var(--footer-success);
      }

      .trust-badge--info svg {
        fill: var(--footer-muted);
      }

      /* Navigation */
      .footer-nav {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 32px;
      }

      .nav-section {
        display: flex;
        flex-direction: column;
        gap: 14px;
      }

      .nav-heading {
        font: 700 11px/1.2 Inter, system-ui, sans-serif;
        text-transform: uppercase;
        letter-spacing: 0.1em;
        color: var(--footer-text);
        opacity: 0.7;
      }

      .nav-links {
        list-style: none;
        display: flex;
        flex-direction: column;
        gap: 10px;
      }

      .nav-links a {
        font: 400 13px/1.4 Inter, system-ui, sans-serif;
        color: var(--footer-color);
        text-decoration: none;
        transition: color 0.15s ease;
      }

      .nav-links a:hover {
        color: var(--footer-text);
      }

      /* Bottom bar */
      .footer-bottom {
        padding: 20px 32px;
        border-top: 1px solid var(--footer-divider);
        display: flex;
        justify-content: space-between;
        align-items: center;
        background: var(--footer-bg2);
        flex-wrap: wrap;
        gap: 16px;
      }

      .credits {
        font: 400 12px/1.4 Inter, system-ui, sans-serif;
        color: var(--footer-color);
      }

      .credits a {
        color: var(--footer-muted);
        text-decoration: none;
        transition: color 0.15s ease;
      }

      .credits a:hover {
        color: var(--footer-text);
      }

      .legal-links {
        display: flex;
        gap: 20px;
        list-style: none;
      }

      .legal-links a {
        font: 400 12px/1 Inter, system-ui, sans-serif;
        color: var(--footer-color);
        text-decoration: none;
        transition: color 0.15s ease;
      }

      .legal-links a:hover {
        color: var(--footer-text);
      }

      /* Legal disclaimer - always visible */
      .disclaimer {
        grid-column: 1 / -1;
        padding: 16px 32px;
        border-top: 1px solid var(--footer-divider);
        background: var(--footer-bg);
      }

      .disclaimer-text {
        font: 500 11px/1.5 Inter, system-ui, sans-serif;
        color: var(--footer-muted);
        text-align: center;
        max-width: 800px;
        margin: 0 auto;
        text-transform: uppercase;
        letter-spacing: 0.02em;
      }

      .consent-status {
        font: 400 12px/1 Inter, system-ui, sans-serif;
        color: var(--footer-color);
        display: flex;
        align-items: center;
        gap: 4px;
      }

      .consent-status button {
        background: none;
        border: none;
        padding: 0;
        font: inherit;
        color: var(--footer-primary);
        cursor: pointer;
        text-decoration: underline;
        text-underline-offset: 2px;
        transition: color 0.15s ease;
      }

      .consent-status button:hover {
        color: var(--footer-text);
      }

      .consent-date {
        color: var(--footer-color);
        cursor: pointer;
        transition: color 0.15s ease;
      }

      .consent-date:hover {
        color: var(--footer-text);
        text-decoration: underline;
        text-underline-offset: 2px;
      }

      /* Responsive */
      @media (max-width: 768px) {
        .cta-banner {
          flex-direction: column;
          text-align: center;
        }

        .footer-main {
          grid-template-columns: 1fr;
          gap: 40px;
          padding: 32px 24px;
        }

        .footer-nav {
          grid-template-columns: repeat(2, 1fr);
        }

        .footer-bottom {
          flex-direction: column;
          text-align: center;
          padding: 20px 24px;
        }

        .legal-links {
          justify-content: center;
        }

        .trust-badges {
          justify-content: center;
        }
      }

      @media (max-width: 480px) {
        .footer-nav {
          grid-template-columns: 1fr;
          gap: 24px;
        }

        .cta-banner {
          padding: 24px;
        }

        .cta-heading {
          font-size: 16px;
        }

        .disclaimer {
          padding: 16px 24px;
        }

        .disclaimer-text {
          font-size: 10px;
        }
      }
    `;
  }

  private getTemplate(): string {
    const currentYear = new Date().getFullYear();

    const ctaBanner = this.hideCta
      ? ""
      : `
      <div class="cta-banner">
        <div class="cta-content">
          <h3 class="cta-heading">${this.escapeHtml(this.ctaHeading)}</h3>
          <p class="cta-subtext">${this.escapeHtml(this.ctaSubtext)}</p>
        </div>
        <ft-button href="${this.escapeHtml(this.ctaHref)}" data-cta>${this.escapeHtml(this.ctaButtonText)}</ft-button>
      </div>
    `;

    return `
      <footer class="footer">
        ${ctaBanner}
        <div class="footer-main">
          <div class="brand-section">
            <div class="brand">
              <div class="logo">${icon("file-text", 24)}</div>
              <div class="brand-info">
                <span class="brand-name">formthirteen</span>
                <span class="brand-sub">Institutional Portfolio Intelligence</span>
              </div>
            </div>
            <p class="tagline">Quarterly holdings reports from hedge funds, asset managers, and institutional investors. Data sourced directly from SEC EDGAR.</p>
            <div class="trust-badges">
              <span class="trust-badge">${icon("check", 16)} Live from EDGAR</span>
              <span class="trust-badge">${icon("check", 16)} Updated as filings are published</span>
              <span class="trust-badge trust-badge--info">${icon("info", 16)} Not affiliated with the SEC</span>
            </div>
          </div>
          <nav class="footer-nav">
            <div class="nav-section">
              <h4 class="nav-heading">Product</h4>
              <ul class="nav-links">
                <li><a href="/" data-search>Search Holdings</a></li>
                <li><a href="/pricing">Pricing</a></li>
                <li><a href="/superinvestors">Superinvestors</a></li>
              </ul>
            </div>
            <div class="nav-section">
              <h4 class="nav-heading">Developers</h4>
              <ul class="nav-links">
                <li><a href="https://financeapis.dev" target="_blank" rel="noopener">API Access</a></li>
                <li><a href="https://financeapis.dev/docs" target="_blank" rel="noopener">Documentation</a></li>
                <li><a href="https://financeapis.dev/pricing" target="_blank" rel="noopener">API Pricing</a></li>
              </ul>
            </div>
            <div class="nav-section">
              <h4 class="nav-heading">Resources</h4>
              <ul class="nav-links">
                <li><a href="https://www.sec.gov/files/form13f.pdf" target="_blank" rel="noopener">About Form 13F</a></li>
                <li><a href="https://www.sec.gov" target="_blank" rel="noopener">SEC EDGAR</a></li>
                <li><a href="/guides/financial-data-sources">Financial Data Sources</a></li>
              </ul>
            </div>
          </nav>
        </div>
        <div class="disclaimer">
          <p class="disclaimer-text">
            Not investment advice. Data provided "as-is" without warranty of accuracy, completeness, or fitness for any purpose.
            Information may contain errors from SEC filings, filer submissions, or data processing.
            You assume all risk. Consult a licensed financial professional before making investment decisions.
          </p>
        </div>
        <div class="footer-bottom">
          <p class="credits">
            &copy; ${currentYear} FormThirteen &middot; Powered by <a href="https://financeapis.dev" target="_blank" rel="noopener">financeapis.dev</a>
          </p>
          <ul class="legal-links">
            <li><a href="/privacy">Privacy</a></li>
            <li><a href="/terms">Terms</a></li>
            <li><a href="/cookies">Cookies</a></li>
            <li class="consent-status" data-consent></li>
          </ul>
        </div>
      </footer>
    `;
  }

  // Precondition: shadowRoot is rendered
  // Postcondition: Event listeners are attached
  private setupEventListeners(): void {
    const ctaBtn = this.shadowRoot?.querySelector("[data-cta]");
    if (ctaBtn) {
      ctaBtn.addEventListener("click", (e) => {
        this.dispatchEvent(
          new CustomEvent("ft-footer-cta-click", {
            bubbles: true,
            composed: true,
            detail: { href: this.ctaHref },
          }),
        );
      });
    }

    const searchLink = this.shadowRoot?.querySelector("[data-search]");
    if (searchLink) {
      searchLink.addEventListener("click", (e) => {
        e.preventDefault();
        window.dispatchEvent(new CustomEvent("ft-spotlight-open"));
      });
    }
  }

  // Precondition: str is a string
  // Postcondition: Returns HTML-safe string
  private escapeHtml(str: string): string {
    const div = document.createElement("div");
    div.textContent = str;
    return div.innerHTML;
  }

  // Precondition: shadowRoot rendered
  // Postcondition: Consent status element updated based on localStorage
  // Invariant: If lastPolicyUpdate is after acceptedDate, re-acknowledgment required
  private updateConsentDisplay(): void {
    const consentEl = this.shadowRoot?.querySelector("[data-consent]");
    if (!consentEl) return;

    const consent = localStorage.getItem(AppFooter.CONSENT_KEY);
    if (consent) {
      try {
        const { timestamp, acceptedDate } = JSON.parse(consent);
        const consentDate = acceptedDate
          ? new Date(acceptedDate)
          : new Date(timestamp);

        // Check if policy was updated after user's last acknowledgment
        const policyDate = this.lastPolicyUpdate;
        if (policyDate && consentDate < policyDate) {
          // Policy updated - require re-acknowledgment
          consentEl.innerHTML =
            '<button type="button" data-accept>Acknowledge (updated)</button>';
          this.setupConsentButton();
          return;
        }

        const formatted = consentDate.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        });
        consentEl.innerHTML = `<button type="button" class="consent-date" data-show-banner title="Click to review">Acknowledged: ${formatted}</button>`;
        this.setupShowBannerButton();
      } catch {
        consentEl.innerHTML =
          '<button type="button" data-accept>Acknowledge</button>';
        this.setupConsentButton();
      }
    } else {
      consentEl.innerHTML =
        '<button type="button" data-accept>Acknowledge</button>';
      this.setupConsentButton();
    }
  }

  // Precondition: Accept button exists in shadow DOM
  // Postcondition: Click listener attached
  private setupConsentButton(): void {
    const btn = this.shadowRoot?.querySelector("[data-accept]");
    if (btn) {
      btn.addEventListener("click", () => this.acceptConsent());
    }
  }

  // Precondition: Show-banner button exists in shadow DOM
  // Postcondition: Click listener attached to show consent banner
  private setupShowBannerButton(): void {
    const btn = this.shadowRoot?.querySelector("[data-show-banner]");
    if (btn) {
      btn.addEventListener("click", () => {
        window.dispatchEvent(new CustomEvent("ft-consent-banner-show"));
      });
    }
  }

  // Precondition: None
  // Postcondition: Consent stored in localStorage, display updated
  private acceptConsent(): void {
    const now = new Date();
    const acceptedDate = now.toISOString().split("T")[0]; // YYYY-MM-DD format
    localStorage.setItem(
      AppFooter.CONSENT_KEY,
      JSON.stringify({
        accepted: true,
        timestamp: now.getTime(),
        acceptedDate: acceptedDate,
      }),
    );
    this.updateConsentDisplay();
    this.dispatchEvent(
      new CustomEvent("ft-consent-accept", {
        bubbles: true,
        composed: true,
        detail: { timestamp: now.getTime(), acceptedDate },
      }),
    );
  }
}

// Prevent duplicate registration

customElements.define("ft-app-footer", AppFooter);
